import React, { useEffect, useState } from "react"
import { getCountryName } from "helpers/countries"
import dayjs from "dayjs"
import DineroFactory from "dinero.js"
import { CustomReasonCode, InputReason, Order } from "lib/types/generated/graphql-types"
import { useMutation, useQuery } from "@apollo/client"

import GET_STORE_GROUP from "../../../../../graphql/queries/store/GetStoreGroup"

import { Header } from "../Shared.styled"
import {
  Blur,
  ButtonWrapper,
  Container,
  OrderLinesLabel,
  ReasonWrapper,
  Row,
  Summary
} from "./OrderSummary.styled"

import Money from "../../../../Money"
import { Item, Label, Value } from "../../../../Ui/List/List"
import FlagIcon from "../../../../Ui/FlagIcon"
import DotLoader from "../../../../Ui/DotLoader"
import CopyButton from "../../../../Ui/CopyButton"
import PrimaryButton from "../../../../Ui/Buttons/PrimaryButton"
import { VariantRow } from "./VariantRow"
import { ShippingRow } from "./ShippingRow"
import CREATE_COMPENSATION from "../../../../../graphql/mutations/order/compensation/CreateCompensation"
import {
  calculateTaxAmount,
  toCompensationOrderLineFromState,
  toCompensationShippingFeeFromState
} from "../Helpers/helpers"
import CompensationTotals from "./CompensationTotals"
import { ReactComponent as PaymentIcon } from "images/icons/credit-card.svg"
import { NoticeBanner } from "../../../../Ui/Messages/NoticeBanner"
import { handleErrorMessages } from "helpers/errors"
import {
  InputOrderLineCompensationWithAmount,
  InputShippingFeeCompensationWithAmount,
  ReasonType
} from "lib/types/order"
import OrderPercentage from "./OrderPercentage"
import { Compensation } from "../../../../../classes/order/Compensation"
import { Delivery } from "../../../../../classes/order/Delivery"
import { Refund } from "../../../../../classes/order/Refund"
import ReasonInput from "../../Reason/ReasonInput"
import { isSkipReason } from "../../../helpers"

type CompensationState = {
  payment?: { amount: number }
  bonus?: { amount: number }
  giftCards?: { amount: number; giftCardId: string }[]
}

type Props = {
  order: Order
  refetch: () => void
  orderLoading: boolean
  customReasons: CustomReasonCode[]
}

export const OrderSummary = ({ order, refetch, customReasons }: Props) => {
  const [compensation, setCompensation] = useState<CompensationState>()
  const [orderPercentage, setOrderPercentage] = useState<number>()
  const [customPercentage, setCustomPercentage] = useState<string>()
  const [reasonType, setReasonType] = useState<ReasonType>()
  const [reason, setReason] = useState<InputReason>({ code: "", cause: "" })

  const deliveries = new Delivery(order.Deliveries)
  const compensations = new Compensation(order.Compensations)
  const refunds = new Refund(order.Refunds)

  const orderLinesInDeliveries = deliveries
    .completedDeliveries()
    ?.flatMap((delivery) => delivery.orderLines)

  const shippingFeesInDeliveries = deliveries
    .completedDeliveries()
    ?.flatMap((delivery) => delivery.shippingFees)

  const totalPayment =
    deliveries.paymentTotal() -
    (compensations.paymentTotal() + refunds.paymentTotal(order.isTaxIncludedInPrice))

  const totalGiftCard =
    deliveries.giftCardTotal() - (compensations.giftCardTotal() + refunds.giftCardTotal())
  const totalBonus = deliveries.bonusTotal() - (compensations.bonusTotal() + refunds.bonusTotal())

  const [inputOrderLines, setInputOrderLines] = useState<InputOrderLineCompensationWithAmount[]>(
    orderLinesInDeliveries.map((deliveryLine) => ({
      orderLineId: deliveryLine.orderLineId,
      quantity: deliveryLine.quantity,
      taxPercentage: deliveryLine.taxPercentage,
      taxPercentageDecimals: deliveryLine.taxPercentageDecimals,
      totalAmount: deliveryLine.totalAmount,
      totalTaxAmount: deliveryLine.totalTaxAmount,
      totalDiscountAmount: deliveryLine.totalDiscountAmount,
      compensationAmount: 0,
      previouslyCompensatedAmount: compensations.orderLineTotal(deliveryLine.orderLineId)
    }))
  )

  const getOrderShippingFee = (shippingFeeId: string) =>
    order?.shippingFees?.find((fee) => fee.id === shippingFeeId)

  const [inputShippingFees, setInputShippingFees] = useState<
    InputShippingFeeCompensationWithAmount[]
  >(
    shippingFeesInDeliveries.map((shippingFee) => ({
      shippingFeeId: shippingFee.shippingFeeId,
      totalAmount: shippingFee.totalAmount,
      compensationAmount: 0,
      taxPercentage: getOrderShippingFee(shippingFee.shippingFeeId)?.taxPercentage ?? 0,
      taxPercentageDecimals:
        getOrderShippingFee(shippingFee.shippingFeeId)?.taxPercentageDecimals ?? 0,
      totalDiscountAmount: getOrderShippingFee(shippingFee.shippingFeeId)?.discountAmount ?? 0,
      totalTaxAmount: getOrderShippingFee(shippingFee.shippingFeeId)?.taxAmount ?? 0,
      previouslyCompensatedAmount: compensations.shippingFeeTotal(
        shippingFee.shippingFeeId,
        (getOrderShippingFee(shippingFee.shippingFeeId)?.basePriceAmount ?? 0) -
          (getOrderShippingFee(shippingFee.shippingFeeId)?.salePriceAmount ?? 0)
      )
    }))
  )

  const getTotalGiftCardAmount = () => {
    return compensation?.giftCards?.reduce((a, b) => a + b.amount, 0) ?? 0
  }

  const getTotalCompensationAmount = () => {
    return (
      (compensation?.payment?.amount ?? 0) +
      (compensation?.bonus?.amount ?? 0) +
      getTotalGiftCardAmount()
    )
  }

  const { data, loading } = useQuery(GET_STORE_GROUP, {
    fetchPolicy: "cache-first",
    variables: { id: order?.storeGroupId }
  })

  const updateCompensationPayment = (amount: number) => {
    if (!amount) {
      const copy = compensation
      delete copy?.payment
      if (copy && Object.keys(copy).length === 0) {
        setCompensation(undefined)
        return
      }
      setCompensation(copy)
      return
    }
    setCompensation({ ...compensation, payment: { amount } })
  }

  const updateCompensationBonus = (amount: number) => {
    if (!amount) {
      const copy = compensation
      delete copy?.bonus
      if (copy && Object.keys(copy).length === 0) {
        setCompensation(undefined)
        return
      }
      setCompensation(copy)
      return
    }
    setCompensation({ ...compensation, bonus: { amount } })
  }

  const updateCompensationGiftCard = (amount: number, giftCardId: string) => {
    const giftCardExists = compensation?.giftCards?.find(
      (giftCard) => giftCard.giftCardId === giftCardId
    )
    setCompensation({
      ...compensation,
      giftCards: giftCardExists
        ? compensation?.giftCards?.map((giftCard) =>
            giftCard.giftCardId === giftCardId
              ? {
                  giftCardId: giftCardId,
                  amount: amount
                }
              : giftCard
          )
        : [...(compensation?.giftCards ?? []), { giftCardId: giftCardId, amount: amount }]
    })
  }

  const shippingFeeRefunded = (shippingFeeId: string) =>
    refunds
      .completedRefunds()
      .find((refund) => refund.shippingFees.find((fee) => fee.shippingFeeId === shippingFeeId))



  const getTotalOrderTotalAmount = () => {
    if (order.isTaxIncludedInPrice) return order.totals.grandTotal
    return order.totals.grandTotal + order.totals.taxTotal
  }

  const getTotalOrderLineAmount = (inputOrderLine: InputOrderLineCompensationWithAmount) => {
    return (
      inputOrderLine.totalAmount -
      (inputOrderLine.totalAmount / inputOrderLine.quantity) *
        refunds.orderLineRefundedQuantity(inputOrderLine.orderLineId)
    )
  }

  const updateOrderLinesAmount = (percentage: number) => {
    if (percentage) {
      setInputOrderLines(
        inputOrderLines
          .filter((orderLine) => !refunds?.orderLineFullyRefunded(orderLine.orderLineId, orderLine.quantity))
          .map((inputOrderLine) => {
            const amount =
              percentage === 100
                ? getTotalOrderLineAmount(inputOrderLine) -
                  inputOrderLine.previouslyCompensatedAmount
                : Math.floor(getTotalOrderLineAmount(inputOrderLine) * (percentage / 100))
            return { ...inputOrderLine, compensationAmount: amount }
          })
      )
      setInputShippingFees(
        inputShippingFees
          .filter((fee) => !shippingFeeRefunded(fee.shippingFeeId))
          .map((inputShippingFee) => {
            const amount =
              percentage === 100
                ? inputShippingFee.totalAmount - inputShippingFee.previouslyCompensatedAmount
                : Math.floor(inputShippingFee.totalAmount * (percentage / 100))
            return {
              ...inputShippingFee,
              compensationAmount: amount
            }
          })
      )
    }
  }

  const clearOrderPercentage = () => {
    setOrderPercentage(undefined)
    setCustomPercentage(undefined)
  }

  const [createCompensation, { loading: createLoading }] = useMutation(CREATE_COMPENSATION, {
    context: {
      skipRumLogging: !order.isTaxIncludedInPrice
    },
    onCompleted: () => {
      setCompensation(undefined)
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const createOrderCompensation = () => {
    createCompensation({
      variables: {
        orderId: order.id,
        input: {
          ...(compensation?.payment && { payment: compensation.payment }),
          ...(compensation?.bonus && { bonus: compensation.bonus }),
          ...(reason.cause && reason.code && !isSkipReason(reasonType) && { reason }),
          ...(compensation?.giftCards &&
            compensation?.giftCards?.length > 0 && { giftCards: compensation.giftCards }),
          ...(inputOrderLines.length > 0 && {
            orderLines: inputOrderLines
              .filter((orderLine) => orderLine.compensationAmount > 0)
              .map((orderLine) => toCompensationOrderLineFromState(orderLine, order.currencyCode))
          }),
          ...(inputShippingFees.length > 0 && {
            shippingFees: inputShippingFees
              .filter((shippingFee) => shippingFee.compensationAmount > 0)
              .map((shippingFee) =>
                toCompensationShippingFeeFromState(shippingFee, order.currencyCode)
              )
          })
        }
      }
    })
    setInputOrderLines(
      inputOrderLines.map((orderLine) => ({ ...orderLine, compensationAmount: 0 }))
    )
    setInputShippingFees(
      inputShippingFees.map((shippingFee) => ({ ...shippingFee, compensationAmount: 0 }))
    )
    setOrderPercentage(undefined)
    window.scrollTo(0, 0)
  }

  const getAmountFromLines = () => {
    const getAmount = (
      line: InputOrderLineCompensationWithAmount | InputShippingFeeCompensationWithAmount
    ) => {
      if (order.isTaxIncludedInPrice) {
        return line.compensationAmount
      }
      return (
        line.compensationAmount +
        calculateTaxAmount(line.compensationAmount, line.taxPercentage, line.taxPercentageDecimals)
      )
    }

    return (
      inputOrderLines.reduce((a, b) => a + (isNaN(b.compensationAmount) ? 0 : getAmount(b)), 0) +
      inputShippingFees.reduce((a, b) => a + (isNaN(b.compensationAmount) ? 0 : getAmount(b)), 0)
    )
  }

  const maxAmount = totalPayment + totalBonus + totalGiftCard

  const previouslyCompensatedB2bOrder =
    compensations.completedCompensations().length > 0 && !order.isTaxIncludedInPrice

  const compensationDisabled = () => {
    if (getTotalCompensationAmount() <= 0) return true
    if (!order.isTaxIncludedInPrice) return false
    if ((!reason.code || !reason.cause) && reasonType !== ReasonType.SKIP) return true

    return (
      (compensation?.payment?.amount ?? 0) > maxAmount ||
      Object.keys(compensation ?? {}).length === 0 ||
      getTotalCompensationAmount() - getAmountFromLines() < 0
    )
  }

  useEffect(() => {
    if (getTotalOrderTotalAmount() >= getAmountFromLines())
      setCompensation({ ...compensation, payment: { amount: getAmountFromLines() } })
  }, [inputOrderLines, inputShippingFees])

  if (!order)
    return (
      <Blur>
        <Header>
          <h2>Order: #1234567</h2>
        </Header>
        <Summary>
          <Item flexibleHeight>
            <Label>E-mail:</Label>
            <Value>
              <>
                john.doe@email.com
                <CopyButton string={""} />
              </>
            </Value>
          </Item>
          <Item>
            <Label>Store group:</Label>
            <Value>store group</Value>
          </Item>
          <Item>
            <Label>Store market:</Label>
            <Value flex>
              <>Sweden</>
            </Value>
          </Item>
          <Item>
            <Label>Order date:</Label>
            <Value>2000-00-00 00:00</Value>
          </Item>
          <Item>
            <Label>Total order value:</Label>
            <Value>SEK 200.00</Value>
          </Item>
        </Summary>
      </Blur>
    )
  if (!order) return null

  return (
    <>
      <Header>
        <h2>Order: #{order?.reference}</h2>
      </Header>
      <Summary>
        <Row>
          <Label>E-mail:</Label>
          <Value>
            <>
              {order?.billingAddress?.email}
              <CopyButton string={order?.billingAddress?.email} />
            </>
          </Value>
        </Row>
        <Row>
          <Label>Store group:</Label>
          <Value>{loading ? <DotLoader /> : data?.getStoreGroup?.name}</Value>
        </Row>
        <Row>
          <Label>Store market:</Label>
          <Value flex>
            <>
              <FlagIcon countryCode={order?.countryCode} />
              {getCountryName(order?.countryCode)}
            </>
          </Value>
        </Row>
        <Row>
          <Label>Order date:</Label>
          <Value>{dayjs(order?.date).format("YYYY-MM-DD HH:mm")}</Value>
        </Row>
        {!previouslyCompensatedB2bOrder && totalGiftCard > 0 ? (
          <Row>
            <Label>Gift card total:</Label>
            <Value>
              <Money
                amount={parseInt(`${totalGiftCard}`)}
                currencyUnit={order?.currencyCode as DineroFactory.Currency}
              />
            </Value>
          </Row>
        ) : (
          <></>
        )}
        {!previouslyCompensatedB2bOrder && totalBonus > 0 ? (
          <Row>
            <Label>Bonus total:</Label>
            <Value>
              <Money
                amount={parseInt(`${totalBonus}`)}
                currencyUnit={order?.currencyCode as DineroFactory.Currency}
              />
            </Value>
          </Row>
        ) : (
          <></>
        )}
        {!previouslyCompensatedB2bOrder && totalPayment > 0 ? (
          <Row>
            <Label>{`Payment total:`}</Label>
            <Value>
              <Money
                amount={parseInt(`${totalPayment}`)}
                currencyUnit={order?.currencyCode as DineroFactory.Currency}
              />
              {!order.isTaxIncludedInPrice && " (Ex. VAT)"}
            </Value>
          </Row>
        ) : (
          <></>
        )}
      </Summary>
      <Container>
        <h2>Compensation</h2>
        <>
          {!compensations.onGoing() && (
            <OrderPercentage
              percentage={orderPercentage}
              customPercentage={customPercentage}
              setCustomPercentage={setCustomPercentage}
              setPercentage={setOrderPercentage}
              updateOrderLinesAmount={updateOrderLinesAmount}
            />
          )}
          <>
            {compensations.onGoing() && (
              <OrderLinesLabel>Or for individual order lines</OrderLinesLabel>
            )}
            {orderLinesInDeliveries
              ?.filter(
                (orderLine) =>
                  !compensations
                    .onGoingCompensations()
                    ?.find((compensation) =>
                      compensation?.orderLines?.find(
                        (compensationOrderLine) =>
                          compensationOrderLine.orderLineId === orderLine.orderLineId
                      )
                    )
              )
              .map((orderLine) => (
                <VariantRow
                  key={orderLine?.orderLineId}
                  orderLine={orderLine}
                  order={order}
                  refetch={refetch}
                  currencyCode={order?.currencyCode}
                  setInputOrderLines={setInputOrderLines}
                  inputOrderLines={inputOrderLines}
                  clearOrderPercentage={clearOrderPercentage}
                  orderPercentage={orderPercentage}
                />
              ))}
            {shippingFeesInDeliveries
              ?.filter(
                (shippingFee) =>
                  !compensations
                    .onGoingCompensations()
                    ?.find((compensation) =>
                      compensation?.shippingFees?.find(
                        (compensationShippingFee) =>
                          compensationShippingFee.shippingFeeId === shippingFee.shippingFeeId
                      )
                    )
              )
              .map((shippingFee) => {
                if (shippingFeeRefunded(shippingFee.shippingFeeId)) return null
                return (
                  <ShippingRow
                    key={shippingFee?.shippingFeeId}
                    shippingFee={shippingFee}
                    order={order}
                    currencyCode={order?.currencyCode}
                    setInputShippingFees={setInputShippingFees}
                    inputShippingFees={inputShippingFees}
                    refetch={refetch}
                    clearOrderPercentage={clearOrderPercentage}
                    orderPercentage={orderPercentage}
                  />
                )
              })}
          </>
          {totalPayment > 0 ||
          totalBonus > 0 ||
          totalGiftCard > 0 ||
          previouslyCompensatedB2bOrder ? (
            orderLinesInDeliveries?.length > 0 || shippingFeesInDeliveries?.length > 0 ? (
              <>
                {!compensations.onGoing() && (
                  <CompensationTotals
                    order={order}
                    updatePayment={updateCompensationPayment}
                    updateGiftCard={updateCompensationGiftCard}
                    updateBonus={updateCompensationBonus}
                    amount={compensation?.payment?.amount || 0}
                    icon={<PaymentIcon />}
                    maxAmount={maxAmount}
                    compensation={compensation}
                    amountFromLines={getAmountFromLines()}
                    totalCompensationAmount={getTotalCompensationAmount()}
                    totalGiftCardAmount={getTotalGiftCardAmount()}
                  />
                )}
              </>
            ) : (
              <div>
                <h2>Order lines ({orderLinesInDeliveries?.length ?? 0})</h2>{" "}
                <NoticeBanner type="important">
                  Missing completed deliveries for orderlines
                </NoticeBanner>
                <h2>Shipping ({shippingFeesInDeliveries?.length ?? 0})</h2>{" "}
                <NoticeBanner type="important">
                  Missing completed deliveries for shipping
                </NoticeBanner>
              </div>
            )
          ) : (
            <div>
              <NoticeBanner type="important">{`Missing amount to compensate`}</NoticeBanner>
            </div>
          )}
        </>
        {!compensations.onGoing() && (
          <ReasonWrapper>
            <b>Reason</b>
            <ReasonInput
              setReason={setReason}
              reason={reason}
              customReasons={customReasons}
              reasonType={reasonType}
              setReasonType={setReasonType}
            />
          </ReasonWrapper>
        )}
        {!compensations.onGoing() && (
          <>
            {totalPayment > 0 ||
            totalBonus > 0 ||
            totalGiftCard > 0 ||
            previouslyCompensatedB2bOrder ? (
              <ButtonWrapper>
                <PrimaryButton
                  handleClick={createOrderCompensation}
                  loading={createLoading}
                  disabled={compensationDisabled()}
                >
                  Create compensation draft
                </PrimaryButton>
              </ButtonWrapper>
            ) : (
              <></>
            )}
          </>
        )}
      </Container>
    </>
  )
}
